import { useHistory } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { showSuccessAlert, showErrorAlert } from "utils/alert";
import CommonForm from "components/common/CommonForm";
import { addNetwork } from "service/networkService";
import { isHttpSuccess } from "utils/functions";
import {
    ADD_NETWORK_SUCCESS_MESSAGE,
    ADD_NETWORK_FAIL_MESSAGE,
    ADD_NETWORK_FAIL_RETRY_MESSAGE,
    NETWORK_ID_VALIDATION_MESSAGE,
    NETWORK_ID_HELPER_TEXT,
    NETWORK_NAME_VALIDATION_MESSAGE,
    NETWORK_NAME_HELPER_TEXT,
} from "./common/messages";

const AddNetwork = () => {
    const history = useHistory();

    const handleSaveNetworkToApi = async (values: Record<string, any>) => {
        const panlNetworkId = values.panlNetworkId;
        const networkName = values.networkName;

        const replaceGatewayWithNetwork = (message: string | undefined | null): string | undefined => {
            if (!message) return message ?? undefined;
            return message.replaceAll(/gateway/gi, "Network");
        };

        const getNetworkErrorMessage = (
            data: any,
            rawMessage: string | undefined | null
        ): string | undefined => {
            const errors = data?.errors;
            if (Array.isArray(errors)) {
                const netIdError = errors.find((err: any) => err?.key === "net_id");
                if (netIdError) {
                    return "Network ID is invalid";
                }
            }

            const normalizedMessage = (rawMessage || "").toString();
            if (/gateway is already registered/i.test(normalizedMessage)) {
                return "This network ID already exists";
            }

            return replaceGatewayWithNetwork(rawMessage);
        };

        try {
            const response: any = await addNetwork(panlNetworkId.trim().toUpperCase(), networkName);
            if (isHttpSuccess(response.status)) {
                showSuccessAlert({
                    message: ADD_NETWORK_SUCCESS_MESSAGE,
                });
                history.push("/networks");
            } else {
                console.error('Else to add network', response);
                const data = response?.data;
                const rawMessage =
                    data?.description ||
                    data?.message ||
                    response?.message;
                const serverMessage = getNetworkErrorMessage(data, rawMessage);
                showErrorAlert({
                    message: serverMessage || ADD_NETWORK_FAIL_MESSAGE,
                });
            }
        } catch (error: any) {
            console.error('Failed to add network', error);
            const data = error?.response?.data;
            const rawMessage =
                data?.description ||
                data?.message ||
                error?.message;
            const serverMessage = getNetworkErrorMessage(data, rawMessage);
            showErrorAlert({
                message: serverMessage || ADD_NETWORK_FAIL_RETRY_MESSAGE,
            });
        }
    };

    return (
        <ContentWrapper>
            <div className="page-content">
                <Container>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">Add Network</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <CommonForm
                                fields={[
                                    {
                                        name: "panlNetworkId",
                                        label: "Network ID",
                                        type: "text",
                                        required: true,
                                        placeholder: "Enter Network ID",
                                        helperText: NETWORK_ID_HELPER_TEXT(),
                                        validate: (value: string) => {
                                            /* istanbul ignore next -- CommonForm text fields always pass string values */
                                            if (typeof value !== "string") return undefined;
                                            const trimmedValue = value.trim();
                                            // Check for leading or trailing spaces
                                            if (value !== trimmedValue) {
                                                return NETWORK_ID_VALIDATION_MESSAGE;
                                            }
                                            // Require exactly 24 characters
                                            if (trimmedValue.length !== 24) {
                                                return NETWORK_ID_VALIDATION_MESSAGE;
                                            }
                                            // Allow alphanumeric, case-insensitive; we will uppercase before API call
                                            if (!/^[a-zA-Z0-9]+$/.test(trimmedValue)) {
                                                return NETWORK_ID_VALIDATION_MESSAGE;
                                            }
                                            return undefined;
                                        }
                                    },
                                    {
                                        name: "networkName",
                                        label: "Network Name",
                                        type: "text",
                                        required: true,
                                        placeholder: "Enter Network Name",
                                        helperText: NETWORK_NAME_HELPER_TEXT(),
                                        validate: (value: string) => {
                                            /* istanbul ignore next -- CommonForm text fields always pass string values */
                                            if (typeof value !== "string") return undefined;
                                            const trimmedValue = value.trim();
                                            // Check minimum length (1 char)
                                            if (trimmedValue.length < 1) {
                                                return NETWORK_NAME_VALIDATION_MESSAGE;
                                            }
                                            // Check maximum length (32 chars)
                                            if (trimmedValue.length > 32) {
                                                return NETWORK_NAME_VALIDATION_MESSAGE;
                                            }
                                            // Check if only alphanumeric characters and spaces (no special characters)
                                            if (!/^[a-zA-Z0-9\s]+$/.test(trimmedValue)) {
                                                return NETWORK_NAME_VALIDATION_MESSAGE;
                                            }
                                            return undefined;
                                        }
                                    }
                                ]}
                                onSubmit={handleSaveNetworkToApi}
                                buttons={[
                                    {
                                        text: "CANCEL",
                                        type: "button",
                                        variant: "secondary",
                                        onClick: () => history.goBack()
                                    },
                                    {
                                        text: "SAVE",
                                        type: "submit",
                                        variant: "primary"
                                    }
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default AddNetwork; 